import { useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink } from "lucide-react";
import {
  api,
  type PublicationsHubChannelFb,
  type PublicationsHubChannelIg,
  type PublicationsHubItem,
} from "@/lib/api";
import type { MetricTipId } from "@/lib/metricsGlossary";
import PageHeader from "@/components/PageHeader";
import FunnelMixBars from "@/components/FunnelMixBars";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Select } from "@/components/ui/select";
import { InfoTip, LabelWithTip } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

const PERIODS: { days: 7 | 14 | 30; label: string }[] = [
  { days: 7, label: "7 dni" },
  { days: 14, label: "14 dni" },
  { days: 30, label: "30 dni" },
];

const KIND_OPTS = [
  { value: "", label: "Wszystkie formaty" },
  { value: "carousel", label: "Karuzela" },
  { value: "feed_post", label: "Post" },
  { value: "czasotekst", label: "Czasotekst" },
];

const FUNNEL_OPTS = [
  { value: "", label: "Cały lejek" },
  { value: "tofu", label: "TOFU" },
  { value: "mofu", label: "MOFU" },
  { value: "bofu", label: "BOFU" },
];

function formatPct(v: number): string {
  return `${Math.round((v || 0) * 1000) / 10}%`;
}

function formatDay(iso: string): string {
  if (!iso || iso.length < 10) return iso || "-";
  return `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}`;
}

function evalTone(label: string): string {
  if (label === "konwertuje" || label === "mocne") return "bg-emerald-50 text-emerald-800 border-emerald-200";
  if (label === "słabo" || label === "ruch bez kliku") return "bg-rose-50 text-rose-800 border-rose-200";
  if (label === "klik bez koszyka" || label === "za wcześnie") return "bg-amber-50 text-amber-900 border-amber-200";
  return "bg-muted text-foreground border-border";
}

function kindLabel(kind: string): string {
  if (kind === "feed_post") return "post";
  if (kind === "czasotekst") return "czasotekst";
  return "karuzela";
}

function funnelBadgeVariant(funnel?: string): "secondary" | "brand" | "warning" | "outline" {
  if (funnel === "mofu") return "brand";
  if (funnel === "bofu") return "warning";
  if (funnel === "tofu") return "secondary";
  return "outline";
}

function funnelTipId(funnel?: string): MetricTipId | undefined {
  if (funnel === "tofu") return "funnel_tofu";
  if (funnel === "mofu") return "funnel_mofu";
  if (funnel === "bofu") return "funnel_bofu";
  return undefined;
}

function Metric({
  label,
  value,
  tipId,
}: {
  label: string;
  value: string | number;
  tipId?: MetricTipId;
}) {
  return (
    <div>
      <div className="flex items-center gap-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
        <span>{label}</span>
        {tipId ? <InfoTip tipId={tipId} /> : null}
      </div>
      <div className="text-sm font-medium tabular-nums">{value}</div>
    </div>
  );
}

function ChannelRow({
  label,
  present,
  children,
}: {
  label: string;
  present: boolean;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-foreground/80">{label}</div>
      {present ? (
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">{children}</div>
      ) : (
        <p className="text-xs text-muted-foreground">brak publikacji na tym kanale</p>
      )}
    </div>
  );
}

function emptyChannelIg(): PublicationsHubChannelIg {
  return {
    present: false,
    impressions: 0,
    reach: 0,
    saved: 0,
    shares: 0,
    comments: 0,
    save_rate: 0,
  };
}

function emptyChannelFb(): PublicationsHubChannelFb {
  return { present: false, impressions: 0, reach: 0, comments: 0, clicks: 0 };
}

function PublicationCard({ item }: { item: PublicationsHubItem }) {
  const ig = item.ig || emptyChannelIg();
  const fb = item.fb || emptyChannelFb();
  const funnelTip = funnelTipId(item.funnel_stage);

  return (
    <Card className="border-border bg-white shadow-sm">
      <CardContent className="grid gap-4 p-4 md:grid-cols-[112px_1fr]">
        <div className="overflow-hidden rounded-md bg-muted">
          {item.thumb_url ? (
            <img
              src={item.thumb_url}
              alt=""
              className="aspect-[4/5] h-full w-full object-cover"
            />
          ) : (
            <div className="flex aspect-[4/5] items-center justify-center text-xs text-muted-foreground">
              brak miniatury
            </div>
          )}
        </div>
        <div className="min-w-0 space-y-3">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0 space-y-1">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="secondary">{kindLabel(item.kind)}</Badge>
                <span className="inline-flex items-center gap-1">
                  <Badge variant={funnelBadgeVariant(item.funnel_stage)}>
                    {item.funnel_stage.toUpperCase()}
                  </Badge>
                  {funnelTip ? <InfoTip tipId={funnelTip} /> : null}
                </span>
                <span className="text-xs text-muted-foreground">{formatDay(item.published_at)}</span>
              </div>
              <h2 className="truncate text-base font-semibold">{item.title || item.id}</h2>
              {item.via ? (
                <p className="flex flex-wrap items-center gap-1 truncate text-xs text-muted-foreground">
                  <span>
                    via {item.via}
                    {item.short_url ? ` · ${item.short_url}` : ""}
                  </span>
                  <InfoTip tipId="pub_via" />
                </p>
              ) : null}
            </div>
            <div className="flex flex-wrap gap-2">
              {item.ig_permalink ? (
                <a
                  href={item.ig_permalink}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-brand hover:underline"
                >
                  IG <ExternalLink className="h-3 w-3" />
                </a>
              ) : null}
              {item.fb_permalink ? (
                <a
                  href={item.fb_permalink}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-brand hover:underline"
                >
                  FB <ExternalLink className="h-3 w-3" />
                </a>
              ) : null}
              <Link to={item.studio_href} className="text-xs text-brand hover:underline">
                Studio
              </Link>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-md border border-border/80 bg-muted/30 p-3">
              <div className="mb-2 flex items-center gap-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                <LabelWithTip tipId="pub_section_reach" label="Zasięg" />
              </div>
              <div className="space-y-3">
                <ChannelRow label="Instagram" present={ig.present}>
                  <Metric label="Reach" value={ig.reach} tipId="pub_ig_reach" />
                  <Metric label="Save" value={ig.saved} tipId="pub_ig_saved" />
                  <Metric label="Share" value={ig.shares} tipId="pub_ig_shares" />
                  <Metric label="Koment." value={ig.comments} tipId="pub_ig_comments" />
                  <Metric label="Save %" value={formatPct(ig.save_rate)} tipId="pub_ig_save_rate" />
                  <Metric
                    label="Δ 1d"
                    value={item.views_delta_1d == null ? "-" : `+${item.views_delta_1d}`}
                    tipId="pub_delta_1d"
                  />
                </ChannelRow>
                <ChannelRow label="Facebook" present={fb.present}>
                  <Metric label="Reach" value={fb.reach} tipId="pub_fb_reach" />
                  <Metric label="Imp." value={fb.impressions} tipId="pub_fb_impressions" />
                  <Metric label="Koment." value={fb.comments} tipId="pub_fb_comments" />
                </ChannelRow>
              </div>
            </div>
            <div className="rounded-md border border-border/80 bg-muted/30 p-3">
              <div className="mb-2 flex items-center gap-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                <LabelWithTip tipId="pub_section_funnel" label="Nasz lejek" />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <Metric label="Sesje" value={item.sessions} tipId="pub_sessions" />
                <Metric label="Koszyki" value={item.checkouts} tipId="pub_checkouts" />
                <Metric label="Zakupy" value={item.purchases} tipId="pub_purchases" />
                <Metric label="CVR koszyk" value={formatPct(item.cvr_checkout)} tipId="pub_cvr_checkout" />
                <Metric label="CVR zakup" value={formatPct(item.cvr_purchase)} tipId="pub_cvr_purchase" />
                <Metric
                  label="Score"
                  value={Math.round((item.score || 0) * 100) / 100}
                  tipId="pub_score"
                />
              </div>
            </div>
          </div>

          <div className={cn("rounded-md border px-3 py-2 text-sm", evalTone(item.eval_label))}>
            <span className="font-medium">{item.eval_label || "OK"}</span>
            {item.eval_text ? <span className="text-foreground/80"> · {item.eval_text}</span> : null}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function Publikacje() {
  const [days, setDays] = useState<7 | 14 | 30>(14);
  const [kind, setKind] = useState("");
  const [funnel, setFunnel] = useState("");

  const query = useQuery({
    queryKey: ["publications-hub", days, kind, funnel],
    queryFn: () =>
      api.publicationsHub({
        days,
        kind: kind || undefined,
        funnel_stage: funnel || undefined,
      }),
    retry: false,
  });

  const data = query.data;
  const kpis = data?.kpis;
  const planVs = data?.plan_vs_live;

  const syncLabel = useMemo(() => {
    if (!data?.synced_at) return "brak syncu Graph";
    return `Graph: ${formatDay(data.synced_at)} ${data.synced_at.slice(11, 16)}`;
  }, [data?.synced_at]);

  return (
    <div>
      <PageHeader
        title="Publikacje"
        subtitle="Jak poszło po publikacji ze studia (zasięg Meta + nasz lejek via)"
        actions={
          <div className="flex flex-wrap gap-2">
            {PERIODS.map((p) => (
              <Button
                key={p.days}
                size="sm"
                variant={days === p.days ? "default" : "outline"}
                onClick={() => setDays(p.days)}
              >
                {p.label}
              </Button>
            ))}
          </div>
        }
      />

      <p className="mb-3 text-xs text-muted-foreground">{syncLabel}</p>

      {query.isLoading ? (
        <p className="text-sm text-muted-foreground">Ładuję hub…</p>
      ) : query.isError ? (
        <p className="text-sm text-rose-600">Nie udało się wczytać publikacji.</p>
      ) : (
        <>
          <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Card className="border-border bg-white shadow-sm">
              <CardContent className="space-y-1 py-4">
                <div className="text-xs uppercase text-muted-foreground">
                  <LabelWithTip tipId="pub_kpi_publications" label="Publikacje" />
                </div>
                <div className="text-2xl font-semibold tabular-nums">{kpis?.publications ?? 0}</div>
                <div className="text-xs text-muted-foreground">
                  Save {kpis?.saved ?? 0} · Share {kpis?.shares ?? 0}
                </div>
              </CardContent>
            </Card>
            <Card className="border-border bg-white shadow-sm">
              <CardContent className="space-y-1 py-4">
                <div className="text-xs uppercase text-muted-foreground">
                  <LabelWithTip tipId="pub_kpi_reach" label="Zasięg" />
                </div>
                <div className="text-2xl font-semibold tabular-nums">
                  IG {(kpis?.reach_ig ?? 0).toLocaleString("pl-PL")}
                </div>
                <div className="text-xs text-muted-foreground">
                  FB {(kpis?.reach_fb ?? 0).toLocaleString("pl-PL")} · Kom. {kpis?.comments ?? 0}
                </div>
              </CardContent>
            </Card>
            <Card className="border-border bg-white shadow-sm">
              <CardContent className="space-y-1 py-4">
                <div className="text-xs uppercase text-muted-foreground">
                  <LabelWithTip tipId="pub_kpi_funnel" label="Nasz lejek" />
                </div>
                <div className="text-2xl font-semibold tabular-nums">{kpis?.sessions ?? 0}</div>
                <div className="text-xs text-muted-foreground">
                  Koszyki {kpis?.checkouts ?? 0} · Zakupy {kpis?.purchases ?? 0}
                </div>
              </CardContent>
            </Card>
            <Card className="border-border bg-white shadow-sm">
              <CardContent className="space-y-1 py-4">
                <div className="text-xs uppercase text-muted-foreground">
                  <LabelWithTip tipId="pub_kpi_cvr" label="CVR / score" />
                </div>
                <div className="text-2xl font-semibold tabular-nums">
                  {formatPct(kpis?.cvr_purchase ?? 0)}
                </div>
                <div className="text-xs text-muted-foreground">
                  Mediana score {Math.round((kpis?.score_median ?? 0) * 100) / 100}
                </div>
              </CardContent>
            </Card>
          </div>

          <Card className="mb-4 border-border bg-white shadow-sm">
            <CardContent className="py-4">
              <FunnelMixBars
                mix={{
                  pct: data?.funnel_mix?.pct,
                  target: data?.funnel_mix?.target,
                  warning: data?.funnel_mix?.warning,
                }}
                themeLine="Mix opublikowanych w oknie vs target 40/40/20"
              />
            </CardContent>
          </Card>

          {planVs?.show ? (
            <Card className="mb-4 border-border bg-white shadow-sm">
              <CardContent className="flex flex-wrap items-center justify-between gap-3 py-3">
                <p className="text-sm">
                  Plan vs live ({planVs.week}):{" "}
                  <span className="font-medium tabular-nums">
                    {planVs.published_studio}/{planVs.planned_studio}
                  </span>{" "}
                  studio opublikowane
                </p>
                <Button asChild size="sm" variant="outline">
                  <Link to={planVs.href}>Plan tygodnia</Link>
                </Button>
              </CardContent>
            </Card>
          ) : null}

          <div className="mb-4 flex flex-wrap gap-2">
            <Select
              value={kind}
              onChange={setKind}
              options={KIND_OPTS}
              className="w-[180px]"
              aria-label="Filtr formatu"
            />
            <Select
              value={funnel}
              onChange={setFunnel}
              options={FUNNEL_OPTS}
              className="w-[160px]"
              aria-label="Filtr lejka"
            />
          </div>

          <div className="space-y-3">
            {(data?.items || []).length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Brak opublikowanych pozycji ze studia w tym oknie.
              </p>
            ) : (
              (data?.items || []).map((item) => <PublicationCard key={item.id} item={item} />)
            )}
          </div>
        </>
      )}
    </div>
  );
}
